import { useState, ChangeEvent, FormEvent } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { SelectOption } from "@idw-types/common";
import { createRelyingPartyReduxRequest } from "@redux-requests/relying-parties";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { OrganizationSelectFormGroup } from "../../common/select-form-groups";
import defaultStyles from "../../common/modals/default-modal/default-modal.less";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";

type AddRelyingPartyModalProps = {
    onModalClose: () => void;
    axnCompanies: Array<AxnCompany>;
    onAddRelyingPartySuccess?: (relyingParty: AxnRelyingParty) => void;
};

type AddRelyingPartyModalState = {
    requestInProgress: boolean;
    relyingPartyName: string;
    selectedRelyingPartyOrganizationOption: SelectOption;
};

export const AddRelyingPartyModal = (props: AddRelyingPartyModalProps) => {
    const dispatch = useDispatch();
    const { onModalClose, axnCompanies, onAddRelyingPartySuccess } = props;
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddRelyingPartyModalState>({
        requestInProgress: false,
        relyingPartyName: "",
        selectedRelyingPartyOrganizationOption: axnCompanies.length === 1 ? mapEntityWithNameAndIdToSelectOption(axnCompanies[0]) : null,
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({
            ...state,
            requestInProgress: true
        });

        const newRelyingParty = await createRelyingPartyReduxRequest(dispatch)(state.selectedRelyingPartyOrganizationOption.value, state.relyingPartyName, axnCompanies);
        if (newRelyingParty) {
            handleModalClose(newRelyingParty);
        } else {
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleModalClose = (newRelyingParty: AxnRelyingParty) => {
        if (onAddRelyingPartySuccess) {
            onAddRelyingPartySuccess(newRelyingParty)
        }
        onModalClose();
     }

    const handleUpdateRelyingPartyName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            relyingPartyName: event.target.value
        });
    };

    const handleUpdateRelyingPartyOrganization = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedRelyingPartyOrganizationOption: newValue
            });
        }
    };

    return (
        <DefaultModal
            automationId="add-relying-party-modal"
            ariaLabelledBy="add-relying-party-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-relying-party-title"} closeButton>
                    <Modal.Title>{resources.modals.addRelyingParty.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span className={defaultStyles.description}>
                        {resources.modals.addRelyingParty.description}
                    </span>
                    <InputFormGroup
                        controlId="relying-party-name-input"
                        label={resources.modals.addRelyingParty.inputs.name.label}
                        ariaLabel={resources.modals.addRelyingParty.inputs.name.label}
                        automationId="relying-party-name-input"
                        placeholder={resources.modals.addRelyingParty.inputs.name.placeholder}
                        value={state.relyingPartyName}
                        onChange={handleUpdateRelyingPartyName}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    <OrganizationSelectFormGroup
                        idPrefix="relying-party"
                        onChange={handleUpdateRelyingPartyOrganization}
                        axnCompanies={axnCompanies}
                        selectedOption={state.selectedRelyingPartyOrganizationOption}
                        disabled={state.requestInProgress || axnCompanies.length === 1}
                        isRequired={true}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-relying-party-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-relying-party-submit-button"
                        disabled={state.requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.common.buttons.save.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};